import DashboardLayout from "cxfcomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "cxfcomponents/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import Card from "@mui/material/Card";
import SoftTypography from "components/SoftTypography";
import { useNavigate } from "react-router-dom";
import StatusBadge from "cxfcomponents/StatusBadge";
import { IntegrationTabs } from "./components/tabs";
import "./style.css";
import { getLocalDateTimeString, formatDateAndTime } from "helpers/common";
import defaultIntegrationIcon from "assets/images/DefaultIntegrationIcon.svg";
import { IntegrationOptions } from "./components/options";
import { useSlugIntegration } from "hooks/useSlugIntegration";
import { useSelector } from "react-redux";
import { useResourceListener } from "hooks/useResourceListener";

const hasCustomIntegrationLogo = (logo) => {
   if (typeof logo === "string") {
      return logo.trim() !== "" && logo !== "null";
   }

   return Boolean(logo);
};

export const LibraryIntegration = () => {
   const navigate = useNavigate();
   const { integration, handleFetchIntegrations } = useSlugIntegration();
   const { users } = useSelector((store) => store.portal.users);
   useResourceListener("integrationsSharingUpdate", handleFetchIntegrations);

   const getOwnerDeveloper = () => {
      return users?.find((user) => user.auth0_id === integration?.create_user_id);
   };

   if (!integration) return <></>;

   const ownerDeveloper = getOwnerDeveloper();
   const hasCustomLogo = hasCustomIntegrationLogo(integration.logo);

   return (
      <>
         <div
            style={{
               padding: "8px",
               position: "sticky",
               top: 0,
               zIndex: 1000,
               backgroundColor: "var(--main-bg-color)"
            }}
         >
            <DashboardNavbar title={integration.integration_name ?? ""} />
         </div>
         <DashboardLayout
            sx={{
               display: "flex",
               flexDirection: "column",
               minHeight: "100vh",
               height: "100vh",
               boxSizing: "border-box",
               overflow: "hidden"
            }}
         >
            <SoftBox
               mt={-3}
               sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0
               }}
            >
               <Card
                  className="flow"
                  sx={{
                     backgroundColor: "transparent !important",
                     overflow: "hidden",
                     boxShadow: "none !important",
                     display: "flex",
                     flexDirection: "column",
                     flex: 1,
                     minHeight: 0
                  }}
               >
                  <Card
                     sx={{
                        overflow: "visible",
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0
                     }}
                  >
                     {/* Top Part */}
                     <SoftBox
                        alignItems="flex-start"
                        justifyContent="space-between"
                        sx={{
                           display: {
                              sm: "block",
                              lg: "flex"
                           },
                           padding: {
                              sm: "12px 32px 20px 32px",
                              lg: "48px 64px 46px 64px"
                           },
                           flexWrap: {
                              sm: "wrap",
                              lg: "noWrap"
                           },
                           columnGap: "40px",
                           height: "max-content"
                        }}
                     >
                        <SoftBox display="flex" alignItems="flex-start" sx={{ marginBottom: { sm: "16px", lg: "0" }, position: "relative" }}>
                           {/* Go Back Button just above the integration logo */}
                           <SoftBox sx={{ position: "absolute", top: "-32px", left: 0 }}>
                              <button
                                 onClick={() => navigate("/workspace/integrations")}
                                 style={{
                                    alignItems: "center",
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    display: "flex",
                                    maxWidth: "max-content",
                                    fontSize: "14px",
                                    padding: 0
                                 }}
                              >
                                 <SoftBox
                                    sx={{
                                       display: "grid",
                                       marginRight: "16px",
                                       placeContent: "center",
                                       fontSize: "28px",
                                       fontWeight: "300"
                                    }}
                                 >
                                    <svg width="10" height="17" viewBox="0 0 10 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                       <path d="M8.77783 0.5L0.999657 8.27817L8.77783 16.0563" stroke="#7B809A" />
                                    </svg>
                                 </SoftBox>
                                 <SoftTypography sx={{ fontWeight: "normal", fontSize: "14px" }}>Go back</SoftTypography>
                              </button>
                           </SoftBox>
                           <SoftBox
                              component="img"
                              borderRadius="16px"
                              marginRight="40px"
                              sx={{
                                 objectFit: "cover",
                                 width: {
                                    sm: "120px",
                                    lg: "174px"
                                 },
                                 minHeight: {
                                    sm: "120px",
                                    lg: "164px"
                                 },
                                 maxHeight: {
                                    sm: "120px",
                                    lg: "164px"
                                 },
                                 border: hasCustomLogo ? "0.5px solid #E6E6E6" : "none"
                              }}
                              src={hasCustomLogo ? `${integration.logo}?v=${integration.updated_date_time ?? ""}` : defaultIntegrationIcon}
                           />
                           <SoftBox
                              sx={{
                                 maxWidth: {
                                    lg: "clamp(100px, 32vw, 540px)"
                                 },
                                 display: "flex",
                                 flexDirection: "column",
                                 minHeight: "164px"
                              }}
                           >
                              <SoftTypography fontSize="24px" fontWeight="bold" lineHeight="1.25" marginBottom="8px" className="white-to-dark">
                                 {integration.integration_name}
                              </SoftTypography>
                              <StatusBadge status={integration.status === "Active" ? "active" : integration.status} />
                              <SoftTypography sx={{ margin: "auto 0", fontSize: "13px" }}>{integration.description ? integration.description : "No description"}</SoftTypography>
                              <IntegrationOptions integration={integration} />
                           </SoftBox>
                        </SoftBox>
                        <SoftBox
                           sx={{
                              flexBasis: {
                                 lg: "max-content"
                              },
                              flexShrink: 0
                           }}
                        >
                           <SoftTypography className="white-to-dark" fontSize="12px" marginBottom="4px">
                              <span style={{ opacity: 0.6, width: "76px", display: "inline-block" }}>Version:</span>
                              <span
                                 style={{
                                    marginLeft: "10px"
                                 }}
                              >
                                 {integration.current_version}
                              </span>
                           </SoftTypography>
                           <SoftTypography sx={{ display: "flex", alignItems: "flexStart" }} className="white-to-dark" fontSize="12px" marginBottom="4px">
                              <span style={{ opacity: 0.6, width: "76px", display: "inline-block" }}>Date Created:</span>
                              <span
                                 style={{
                                    marginLeft: "10px",
                                    maxWidth: "100px",
                                    whiteSpace: "break-spaces"
                                 }}
                              >
                                 {formatDateAndTime(getLocalDateTimeString(integration.created_date_time)) || ""}
                              </span>
                           </SoftTypography>
                           <SoftTypography sx={{ display: "flex", alignItems: "flexStart" }} className="white-to-dark" fontSize="12px" marginBottom="4px">
                              <span style={{ opacity: 0.6, width: "76px", display: "inline-block" }}>Last Edited:</span>
                              <span
                                 style={{
                                    marginLeft: "10px",
                                    maxWidth: "100px",
                                    whiteSpace: "break-spaces"
                                 }}
                              >
                                 {/* You can add last edited logic here if available */}
                              </span>
                           </SoftTypography>
                           <SoftBox display="flex" alignItems="center">
                              <SoftTypography className="white-to-dark" opacity={0.6} fontSize="12px">
                                 Developed By:
                              </SoftTypography>
                              {ownerDeveloper && (
                                 <>
                                    {ownerDeveloper.image && ownerDeveloper.image.trim() !== "" ? (
                                       <SoftBox
                                          component="img"
                                          src={ownerDeveloper.image}
                                          alt={`${ownerDeveloper.first_name} ${ownerDeveloper.last_name}`}
                                          sx={{
                                             width: "24px",
                                             height: "24px",
                                             borderRadius: "32px",
                                             border: ".5px solid white !important",
                                             objectFit: "cover",
                                             margin: "0 8px"
                                          }}
                                       />
                                    ) : (
                                       <SoftTypography component="span" className="white-to-dark" fontSize="12px" sx={{ textDecoration: "underline" }}>
                                          {ownerDeveloper.first_name}
                                       </SoftTypography>
                                    )}
                                    <SoftTypography component="span" className="white-to-dark" fontSize="12px" sx={{ textDecoration: "underline" }}>
                                       {ownerDeveloper.first_name} {ownerDeveloper.last_name}
                                    </SoftTypography>
                                 </>
                              )}
                           </SoftBox>
                        </SoftBox>
                     </SoftBox>
                     {/* End of Top Part */}
                     <SoftBox sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                        <IntegrationTabs />
                     </SoftBox>
                  </Card>
               </Card>
            </SoftBox>
         </DashboardLayout>
      </>
   );
};
